import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  Button, 
  Divider,
  Card,
  Row,
  Col,
  Alert,
  Spin
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  BarChartOutlined
} from '@ant-design/icons';
import { CONFIGURACION_MEDIDOR_DEFAULT } from '../../types/graficasPersonalizadas';
import './FormularioGrafica.scss';

const { Option } = Select;
const { TextArea } = Input;

// Componente personalizado para selector de color
const ColorInput = ({ value, onChange, ...props }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Input
        type="color"
        value={value || '#1890ff'}
        onChange={(e) => onChange && onChange(e.target.value)}
        style={{ width: 50, height: 32, padding: 0, border: '1px solid #d9d9d9' }}
        {...props}
      />
      <Input
        value={value || '#1890ff'}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder="#1890ff"
        style={{ flex: 1 }}
      />
    </div>
  );
};

const FormularioGrafica = ({
  visible,
  onCancel,
  onSubmit,
  grafica = null,
  sensoresDisponibles = [],
  loading = false,
  loadingSensores = false
}) => {
  const [form] = Form.useForm();
  const [configuracion, setConfiguracion] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!grafica;

  // Inicializar formulario cuando se abre
  useEffect(() => {
    if (visible) {
      if (isEditing) {
        // Modo edición
        const valores = {
          titulo: grafica.titulo,
          descripcion: grafica.descripcion,
          sensor: grafica.sensor._id,
          ...grafica.configuracion
        };
        
        form.setFieldsValue(valores);
        setConfiguracion(grafica.configuracion || {});
      } else {
        // Modo creación
        form.resetFields();
        setConfiguracion(CONFIGURACION_MEDIDOR_DEFAULT);
        
        // Establecer valores por defecto
        form.setFieldsValue({
          ...CONFIGURACION_MEDIDOR_DEFAULT
        });
      }
    }
  }, [visible, isEditing, grafica, form]);

  // Manejar cambios en la configuración
  const handleConfiguracionChange = (campo, valor) => {
    const nuevaConfiguracion = {
      ...configuracion,
      [campo]: valor
    };
    
    setConfiguracion(nuevaConfiguracion);
    form.setFieldsValue({ [campo]: valor });
  };

  // Manejar envío del formulario
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const valores = await form.validateFields();
      
      // Preparar datos para envío
      const datosGrafica = {
        titulo: valores.titulo,
        descripcion: valores.descripcion,
        tipoGrafica: 'medidor', // Solo medidores
        tipoAgregacion: 'promedio_diario', // Solo promedio diario
        sensor: valores.sensor,
        configuracion: {
          mostrarLimites: false,
          colorBarra: valores.colorBarra,
          mostrarPorcentaje: false,
          formatoValor: valores.formatoValor
        }
      };

      await onSubmit(datosGrafica);
    } catch (error) {
      console.error('Error en validación del formulario:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isEditing ? <EditOutlined /> : <PlusOutlined />}
          <span>{isEditing ? 'Editar Medidor' : 'Nuevo Medidor Personalizado'}</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={700}
      className="formulario-grafica"
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={submitting}
          onClick={handleSubmit}
        >
          {isEditing ? 'Actualizar' : 'Crear'} Medidor
        </Button>
      ]}
    >
      <Spin spinning={loading || loadingSensores}>
        <Form
          form={form}
          layout="vertical"
          className="formulario-grafica__form"
        >
          {/* Información básica */}
          <Card title="Información Básica" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="titulo"
                  label="Título del Medidor"
                  rules={[
                    { required: true, message: 'El título es requerido' },
                    { max: 100, message: 'Máximo 100 caracteres' }
                  ]}
                >
                  <Input placeholder="Ej: Temperatura Ambiente" />
                </Form.Item>
              </Col>
              
              <Col xs={24} sm={12}>
                <Form.Item
                  name="sensor"
                  label="Sensor"
                  rules={[{ required: true, message: 'Debe seleccionar un sensor' }]}
                >
                  <Select
                    placeholder="Seleccionar sensor"
                    showSearch
                    loading={loadingSensores}
                    disabled={isEditing} // No permitir cambiar sensor en edición
                    filterOption={(input, option) => {
                      const sensor = sensoresDisponibles.find(s => s._id === option.value);
                      if (!sensor) return false;

                      const searchText = input.toLowerCase();
                      return (
                        sensor.alias.toLowerCase().includes(searchText) ||
                        sensor.nombre.toLowerCase().includes(searchText) ||
                        sensor.tipo.nombre.toLowerCase().includes(searchText) ||
                        sensor.unidades.toLowerCase().includes(searchText)
                      );
                    }}
                    optionLabelProp="label"
                  >
                    {sensoresDisponibles.map(sensor => (
                      <Option
                        key={sensor._id}
                        value={sensor._id}
                        label={`${sensor.alias} - ${sensor.nombre}`}
                      >
                        <div className="sensor-option">
                          <div className="sensor-option__main">
                            <span className="sensor-option__alias">{sensor.alias}</span>
                            <span className="sensor-option__separator"> - </span>
                            <span className="sensor-option__nombre">{sensor.nombre}</span>
                          </div>
                          <div className="sensor-option__details">
                            <span className="sensor-option__tipo">{sensor.tipo.nombre}</span>
                            <span className="sensor-option__separator"> • </span>
                            <span className="sensor-option__rango">
                              {sensor.rangoMin}-{sensor.rangoMax} {sensor.unidades}
                            </span>
                            {!sensor.estatusDispositivo && (
                              <>
                                <span className="sensor-option__separator"> • </span>
                                <span className="sensor-option__desconectado">DESCONECTADO</span>
                              </>
                            )}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              
              <Col xs={24}>
                <Form.Item
                  name="descripcion"
                  label="Descripción (Opcional)"
                  rules={[{ max: 300, message: 'Máximo 300 caracteres' }]}
                >
                  <TextArea 
                    rows={2} 
                    placeholder="Descripción opcional del medidor"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Divider />

          {/* Configuración del medidor */}
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChartOutlined />
                <span>Configuración del Medidor</span>
              </div>
            }
            size="small"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="colorBarra"
                  label="Color del Medidor"
                  tooltip="Color principal del medidor y indicadores"
                >
                  <ColorInput
                    value={configuracion.colorBarra}
                    onChange={(color) => handleConfiguracionChange('colorBarra', color)}
                  />
                </Form.Item>
              </Col>
              
              <Col xs={24} sm={12}>
                <Form.Item
                  name="formatoValor"
                  label="Formato de Valores"
                  tooltip="Cómo se muestran los números en el medidor"
                >
                  <Select 
                    onChange={(value) => handleConfiguracionChange('formatoValor', value)}
                  >
                    <Option value="decimal">Decimal (25.4)</Option>
                    <Option value="entero">Entero (25)</Option>
                    <Option value="cientifico">Científico (2.5e+1)</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Información adicional */}
          {sensoresDisponibles.length === 0 && !loadingSensores && (
            <Alert
              message="No hay sensores disponibles"
              description="No se encontraron sensores disponibles para crear medidores. Verifique que tenga sensores configurados en su cuenta."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default FormularioGrafica;